import type { Notification as AgentNotification } from "@ark-bot/protocol";

import { apiBase, configuredHost } from "./host.js";
import { text } from "./i18n.js";
import { NOTIFICATIONS } from "./text/app.js";

/**
 * OS notifications (ARCHITECTURE §6.14). One stream for every agent, so the
 * one you are *not* looking at is the one that can reach you — that is the
 * whole point of the feature, and why it is not derived from the per-agent
 * event stream the chat already has.
 *
 * Two courtesies the browser does not enforce: nothing is raised while the tab
 * is visible, since you are already looking at it, and a second notification
 * from the same agent replaces the first rather than stacking.
 */

const BASE = apiBase(configuredHost());

export type NotificationPermissionState = "unsupported" | "default" | "granted" | "denied";

export function notificationPermission(): NotificationPermissionState {
  if (typeof Notification === "undefined") return "unsupported";
  return Notification.permission;
}

/** Asks the browser, which only allows it from a click. Answers what it decided. */
export async function askForNotificationPermission(): Promise<NotificationPermissionState> {
  if (typeof Notification === "undefined") return "unsupported";
  if (Notification.permission !== "default") return Notification.permission;
  try {
    return await Notification.requestPermission();
  } catch {
    // Older browsers hand back a callback rather than a promise; treat that as no answer.
    return Notification.permission;
  }
}

export interface NotificationStream {
  close(): void;
}

/**
 * Subscribes to the host's notification stream. `onNotification` is called for
 * every agent; the caller decides whether to raise anything, because only it
 * knows which agent is on screen.
 */
export function subscribeNotifications(onNotification: (notification: AgentNotification) => void): NotificationStream {
  const source = new EventSource(`${BASE}/notifications`);
  let closed = false;
  source.addEventListener("notification", (message) => {
    if (closed) return;
    try {
      onNotification(JSON.parse((message as MessageEvent<string>).data) as AgentNotification);
    } catch {
      // A frame we cannot parse is not worth breaking the stream over; the
      // next one will be fine, and nothing here is load-bearing.
    }
  });
  return {
    close: () => {
      closed = true;
      source.close();
    },
  };
}

/** Raises one, if the browser will let us. Returns whether it went out. */
export function raise(notification: AgentNotification, onClick: (agentId: string) => void): boolean {
  if (typeof Notification === "undefined" || Notification.permission !== "granted") return false;
  const title = notification.kind === "needs_input" ? text(NOTIFICATIONS).needsYou(notification.agentName) : notification.agentName;
  // The tag makes a second notification from one agent replace its first,
  // rather than stacking a column of them while the user is away.
  const shown = new Notification(title, { body: notification.body, tag: `ark-bot:${notification.agentId}` });
  shown.onclick = () => {
    window.focus();
    onClick(notification.agentId);
    shown.close();
  };
  return true;
}
